import { Badge, Text } from '@axilon-apps/libs-ui-shadcn'
import {
  ColumnDef,
  TableActionColumnButton,
  UserAvatar,
  colWidth
} from '@axilon-apps/libs-ui-components'
import { Model } from '@axilon-apps/domains-identity/types'
import { UserRoleChip } from '@axilon-apps/domains-identity'
import { formatDate } from '@axilon-apps/libs-ui-utils'

const renderRoleChips = (roleAssignments: Model.UserRoleAssignment[]) => {
  if (!roleAssignments?.length) {
    return <Badge variant="secondary">Not assigned</Badge>
  }

  return roleAssignments.map((assignment) => (
    <UserRoleChip key={assignment.role.id} role={assignment.role.name} size="sm" />
  ))
}

export interface ExtraRowAction {
  label: string
  onClick: (user: Model.User) => void
  /** When provided, decides per-row whether the action is enabled. */
  visible?: (user: Model.User) => boolean
}

interface RowMenuItem {
  label: string
  onClick: (user: Model.User) => void
}

interface StatusActionOptions {
  canManageStatus: boolean
  currentUserId: string
  onDisableUser: (user: Model.User) => void
  onEnableUser: (user: Model.User) => void
}

interface RowMenuOptions extends StatusActionOptions {
  onChangePassword: (user: Model.User) => void
  onRenameUser: (user: Model.User) => void
  onViewUuid: (user: Model.User) => void
  canRenameUser: boolean
  canResetPassword: boolean
  isGlobalAdmin: boolean
  extraActions?: ExtraRowAction[]
}

/**
 * Decides the enable/disable row action for a user. Returns `null` when the
 * action should be hidden — the caller lacks the permission, or it would let a
 * Global Admin disable their own account (self-lockout guard).
 */
export const statusActionFor = (
  user: Model.User,
  { canManageStatus, currentUserId, onDisableUser, onEnableUser }: StatusActionOptions
): RowMenuItem | null => {
  if (!canManageStatus) return null
  // List-users omits `enabled` when the caller lacks User:read.
  if (typeof user.enabled !== 'boolean') return null

  if (user.enabled) {
    if (user.id === currentUserId) return null
    return { label: 'Disable', onClick: onDisableUser }
  }

  return { label: 'Enable', onClick: onEnableUser }
}

interface Props {
  onChangePassword: (user: Model.User) => void
  onRenameUser: (user: Model.User) => void
  onViewUuid: (user: Model.User) => void
  onDisableUser: (user: Model.User) => void
  onEnableUser: (user: Model.User) => void
  canRenameUser: boolean
  canResetPassword: boolean
  canManageStatus: boolean
  isGlobalAdmin: boolean
  currentUserId: string
  hasData: boolean
  showEmail: boolean
  /** Optional extra row-menu actions appended after Reset password. */
  extraActions?: ExtraRowAction[]
  /** Optional host-app columns inserted after the Role column (e.g. devops
      injects a machine-access summary; identity knows nothing about machines). */
  extraColumns?: ColumnDef<Model.User>[]
}

const isLocalUser = (user: Model.User) =>
  user.ldap != null && user.oidc != null && !user.ldap.enabled && !user.oidc.enabled

const enabledBadge = (enabled: boolean | undefined, yes: string, no: string) => {
  if (typeof enabled !== 'boolean') {
    return <Badge variant="outline">—</Badge>
  }

  return (
    <Badge
      variant="outline"
      className={
        enabled
          ? 'bg-st-run/15 text-st-run border-st-run/30'
          : 'bg-st-down/15 text-st-down border-st-down/30'
      }
    >
      {enabled ? yes : no}
    </Badge>
  )
}

export const rowMenuItemsFor = (user: Model.User, options: RowMenuOptions): RowMenuItem[] => {
  const menuItems: RowMenuItem[] = []

  if (options.isGlobalAdmin) {
    menuItems.push({ label: 'View UUID', onClick: options.onViewUuid })
  }

  if (options.canRenameUser && isLocalUser(user)) {
    menuItems.push({ label: 'Edit', onClick: options.onRenameUser })
  }

  if (options.canResetPassword && isLocalUser(user)) {
    menuItems.push({ label: 'Reset password', onClick: options.onChangePassword })
  }

  const statusAction = statusActionFor(user, {
    canManageStatus: options.canManageStatus,
    currentUserId: options.currentUserId,
    onDisableUser: options.onDisableUser,
    onEnableUser: options.onEnableUser
  })
  if (statusAction) {
    menuItems.push(statusAction)
  }

  for (const action of options.extraActions ?? []) {
    if (!action.visible || action.visible(user)) {
      menuItems.push({ label: action.label, onClick: action.onClick })
    }
  }

  return menuItems
}

const UsersTableColumnsConfig = ({
  onChangePassword,
  onRenameUser,
  onViewUuid,
  onDisableUser,
  onEnableUser,
  canRenameUser,
  canResetPassword,
  canManageStatus,
  isGlobalAdmin,
  currentUserId,
  hasData,
  showEmail,
  extraActions,
  extraColumns
}: Props) => {
  const columns: ColumnDef<Model.User>[] = [
    {
      name: 'Avatar',
      formatter: (user: Model.User) => <UserAvatar user={user} size={32} />,
      hideName: true,
      width: colWidth(hasData, '60px')
    },
    {
      name: 'Username',
      formatter: ({ username }: Model.User) => <Text variant="body-sm">{username}</Text>,
      width: colWidth(hasData, '200px')
    },
    {
      name: 'Name',
      formatter: ({ name }: Model.User) => <Text variant="body-sm">{name}</Text>,
      width: colWidth(hasData, '180px')
    },
    {
      name: 'Role',
      formatter: ({ roleAssignments }: Model.User) => (
        <div className="flex flex-row flex-wrap items-center gap-1">
          {renderRoleChips(roleAssignments)}
        </div>
      ),
      width: colWidth(hasData, '200px')
    },
    {
      name: 'Created at',
      formatter: ({ createdAt }: Model.User) => formatDate(createdAt),
      width: colWidth(hasData, '140px')
    },
    {
      name: 'MFA enabled',
      formatter: ({ mfa }: Model.User) => enabledBadge(mfa?.enabled, 'Yes', 'No'),
      width: colWidth(hasData, '100px')
    },
    {
      name: 'Status',
      formatter: ({ enabled }: Model.User) => enabledBadge(enabled, 'Active', 'Disabled'),
      width: colWidth(hasData, '100px')
    },
    {
      name: 'Actions',
      formatter: (user: Model.User) => (
        <TableActionColumnButton
          item={user}
          menuItems={rowMenuItemsFor(user, {
            onChangePassword,
            onRenameUser,
            onViewUuid,
            onDisableUser,
            onEnableUser,
            canRenameUser,
            canResetPassword,
            canManageStatus,
            isGlobalAdmin,
            currentUserId,
            extraActions
          })}
        />
      ),
      hideName: true,
      width: colWidth(hasData, '80px')
    }
  ]

  if (showEmail) {
    columns.splice(3, 0, {
      name: 'Email',
      formatter: ({ email }: Model.User) => <Text variant="body-sm">{email || '—'}</Text>,
      width: colWidth(hasData, '220px')
    })
  }

  if (extraColumns?.length) {
    const roleIndex = columns.findIndex((c) => c.name === 'Role')
    columns.splice(roleIndex + 1, 0, ...extraColumns)
  }

  return columns
}

export default UsersTableColumnsConfig
